import { instanceGuard } from '@superbet-group/web.lib.utilities';

import { transformOfferEvent } from '@core/mappers/offer/transformOfferEvent';
import OfferEvent from '@core/models/offer/OfferEvent';
import { StructManager } from '@core/services/offer/StructManager';
import RestService from '@core/services/results/_private/RestService';

import Singleton from '../common/Singleton';
import SingletonError from '../common/errors/SingletonError';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { getOfferHost } from '../offer/helpers';
import { OfferEventDto } from '@core/mappers/offer/dtos/OfferEventDto';
import { FeatureToggleService } from '@superbet-group/web.lib.feature-toggle-service';

class ResultsService {
    private static instance?: ResultsService;
    private featureFlagService: FeatureToggleService;

    public static getInstance(): ResultsService {
        return instanceGuard(ResultsService.instance);
    }

    public static createInstance() {
        if (!ResultsService.instance) {
            ResultsService.instance = new ResultsService();
        }
        return ResultsService.instance;
    }

    private restService: RestService;

    private constructor() {
        if (ResultsService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.restService = new RestService(getOfferHost());
        this.featureFlagService = featureFlagService;
    }

    public setRestService(restService: RestService) {
        this.restService = restService;
    }

    // for testing
    public setFeatureFlagService(featureFlagService: typeof this.featureFlagService) {
        this.featureFlagService = featureFlagService;
    }

    // ============================
    // Rest methods
    // ============================

    public async getMatchResults(startDate: Date, endDate: Date, sportId?: number): Promise<OfferEvent[]> {
        const response = await this.restService.getMatchResults<OfferEventDto>({
            startDate,
            endDate,
            sportId,
        });
        const matches = response.data.map(transformOfferEvent);
        await StructManager.getInstance().denormalizeEvents(matches);
        return matches;
    }

    public async getMatchResultDetails(matchIds: number[], oddsResults = true): Promise<OfferEvent[]> {
        const responses = await Promise.all(matchIds.map((matchId) => this.restService.getMatchResultDetails<OfferEventDto[]>(matchId, oddsResults)));
        const matches = responses
            .filter((matchResponse) => matchResponse.data.length)
            .map((matchResponse) => {
                return transformOfferEvent(matchResponse.data[0]);
            });
        await StructManager.getInstance().denormalizeEvents(matches);
        return matches;
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete ResultsService.instance;
    }
}

export interface IResultService extends ResultsService {}
export default ResultsService as Singleton<ResultsService>;
